import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { IRootState } from "../../app/store";
import { IRoutingGraph } from "@multiconsult-group/multiplacer-core";

export interface IUnsavedRoutingItem {
  localId: string;
  routingGraph: IRoutingGraph;
  createdAtIso: string;
}

export interface IUnsavedRoutingState {
  items: IUnsavedRoutingItem[];
}

export const AUTO_PIPE_ROUTING_UNSAVED_LOCAL_ID_PREFIX = "auto-pipe-routing:";

const initialState: IUnsavedRoutingState = {
  items: [],
};

export const unsavedRoutingSlice = createSlice({
  name: "unsavedRouting",
  initialState,
  reducers: {
    setUnsavedRoutingItems(state, action: PayloadAction<IUnsavedRoutingItem[]>) {
      state.items = action.payload;
    },
    addUnsavedRoutingItem(state, action: PayloadAction<IUnsavedRoutingItem>) {
      state.items.push(action.payload);
    },
    upsertUnsavedRoutingItem(state, action: PayloadAction<IUnsavedRoutingItem>) {
      const index = state.items.findIndex((item) => item.localId === action.payload.localId);
      if (index === -1) {
        state.items.push(action.payload);
        return;
      }
      state.items[index] = action.payload;
    },
    updateUnsavedRoutingGraphByLocalId(
      state,
      action: PayloadAction<{ localId: string; routingGraph: IRoutingGraph }>
    ) {
      const item = state.items.find((x) => x.localId === action.payload.localId);
      if (!item) return;
      item.routingGraph = action.payload.routingGraph;
    },
    removeUnsavedRoutingItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.localId !== action.payload);
    },
    clearUnsavedRoutingItems(state) {
      state.items = [];
    },
  },
});

export const {
  setUnsavedRoutingItems,
  addUnsavedRoutingItem,
  upsertUnsavedRoutingItem,
  updateUnsavedRoutingGraphByLocalId,
  removeUnsavedRoutingItem,
  clearUnsavedRoutingItems,
} = unsavedRoutingSlice.actions;

export const selectUnsavedRoutingState = (state: IRootState): IUnsavedRoutingState =>
  state.unsavedRouting;

export const selectUnsavedRoutingItems = (state: IRootState): IUnsavedRoutingItem[] =>
  state.unsavedRouting.items;

export const selectHasPendingAutoPipeRoutingPublish = (state: IRootState): boolean =>
  state.unsavedRouting.items.some((item) =>
    item.localId.startsWith(AUTO_PIPE_ROUTING_UNSAVED_LOCAL_ID_PREFIX)
  );

export default unsavedRoutingSlice.reducer;
